/**
 * Section Type Rules Engine
 * Story 254: Change a tech design section's type
 *
 * Pure functions for section type management:
 * - Anchor generation from titles (with collision handling)
 * - Type inference from anchor presence (for migration)
 * - Type-to-anchor relationship rules
 */
/**
 * Convert a title to a kebab-case anchor suitable for capability IDs.
 *
 * Rules:
 * - Lowercase
 * - Replace spaces and underscores with hyphens
 * - Remove non-alphanumeric characters (except hyphens)
 * - Collapse multiple hyphens to single
 * - Trim leading/trailing hyphens
 * - Empty result becomes 'section'
 *
 * @param title - Section title to convert
 * @returns Kebab-case anchor string
 */
export function titleToAnchor(title) {
    const DEFAULT_ANCHOR = 'section';
    if (!title || title.trim().length === 0) {
        return DEFAULT_ANCHOR;
    }
    const anchor = title
        .toLowerCase()
        .trim()
        .replace(/[\s_]+/g, '-') // spaces and underscores -> hyphens
        .replace(/[^a-z0-9-]/g, '') // remove non-alphanumeric (except hyphens)
        .replace(/-+/g, '-') // collapse multiple hyphens
        .replace(/^-|-$/g, ''); // trim leading/trailing hyphens
    return anchor.length > 0 ? anchor : DEFAULT_ANCHOR;
}
/**
 * Generate a unique anchor from a title, handling collisions with existing anchors.
 *
 * If the base anchor already exists, appends a numeric suffix (-2, -3, etc.)
 * until a unique anchor is found.
 *
 * @param title - Section title to convert
 * @param existingAnchors - Array of anchors already in use in the document
 * @returns Generated anchor and whether a collision was detected
 */
export function generateAnchor(title, existingAnchors) {
    const baseAnchor = titleToAnchor(title);
    const existingSet = new Set(existingAnchors);
    // No collision - use base anchor
    if (!existingSet.has(baseAnchor)) {
        return { anchor: baseAnchor, hadCollision: false };
    }
    // Collision - find next available suffix
    const INITIAL_SUFFIX = 2;
    let suffix = INITIAL_SUFFIX;
    let candidateAnchor = `${baseAnchor}-${suffix}`;
    while (existingSet.has(candidateAnchor)) {
        suffix++;
        candidateAnchor = `${baseAnchor}-${suffix}`;
    }
    return { anchor: candidateAnchor, hadCollision: true };
}
/**
 * Determine if a section type should have an anchor.
 *
 * Only 'capability' sections have anchors. Anchors enable:
 * - Task linkage (implements field)
 * - Coverage tracking (G2 gate)
 * - Test requirement association
 *
 * @param sectionType - The section type to check
 * @returns true if the type should have an anchor
 */
export function shouldHaveAnchor(sectionType) {
    return sectionType === 'capability';
}
/**
 * Infer section type from anchor presence (for migration).
 *
 * Used to infer types for existing sections that may not have
 * an explicit section_type field set:
 * - Sections WITH anchors -> 'capability'
 * - Sections WITHOUT anchors -> 'overview' (Informational)
 *
 * Note: 'test_strategy' cannot be inferred - it must be explicitly set.
 *
 * @param anchor - The section's anchor value (may be null/undefined)
 * @returns Inferred section type
 */
export function inferTypeFromAnchor(anchor) {
    // Anchor present means capability
    if (anchor && anchor.trim().length > 0) {
        return 'capability';
    }
    // No anchor defaults to overview (informational)
    return 'overview';
}
/**
 * Validate a type transition and determine required anchor changes.
 *
 * Returns what anchor action is needed when changing section type:
 * - 'generate': Need to generate a new anchor (-> capability)
 * - 'remove': Need to remove the anchor (-> overview or test_strategy)
 * - 'none': No anchor change needed
 *
 * @param fromType - Current section type (or inferred from anchor)
 * @param toType - Target section type
 * @param currentAnchor - Current anchor value
 * @returns Required anchor action
 */
export function getAnchorAction(fromType, toType, currentAnchor) {
    const needsAnchor = shouldHaveAnchor(toType);
    const hasAnchor = currentAnchor && currentAnchor.trim().length > 0;
    if (needsAnchor && !hasAnchor) {
        return 'generate';
    }
    if (!needsAnchor && hasAnchor) {
        return 'remove';
    }
    return 'none';
}
/** Display labels for section types in UI */
export const SECTION_TYPE_LABELS = {
    // Design types
    overview: 'Informational',
    capability: 'Capability',
    test_strategy: 'Testing',
    // PRD types (Story 293)
    requirement: 'Requirement',
    informational: 'Information Only',
};
/** Tooltips for section type options in UI */
export const SECTION_TYPE_TOOLTIPS = {
    // Design types
    overview: 'Useful for context but no work planned against this section',
    capability: 'Testable functionality that tasks can implement',
    test_strategy: 'Contributes to how this story will be tested',
    // PRD types (Story 293)
    requirement: 'Must be covered by tech design (G1 gate enforced)',
    informational: 'Background context only (excluded from G1 gate)',
};
/** Design-only labels for dropdown */
export const DESIGN_TYPE_LABELS = {
    overview: 'Informational',
    capability: 'Capability',
    test_strategy: 'Testing',
};
/** PRD-only labels for dropdown */
export const PRD_TYPE_LABELS = {
    requirement: 'Requirement',
    informational: 'Information Only',
    test_strategy: 'Testing',
};
/** Design-only tooltips */
export const DESIGN_TYPE_TOOLTIPS = {
    overview: 'Useful for context but no work planned against this section',
    capability: 'Testable functionality that tasks can implement',
    test_strategy: 'Contributes to how this story will be tested',
};
/** PRD-only tooltips */
export const PRD_TYPE_TOOLTIPS = {
    requirement: 'Must be covered by tech design (G1 gate enforced)',
    informational: 'Background context only (excluded from G1 gate)',
    test_strategy: 'Contributes to how this story will be tested',
};
/**
 * Infer PRD section type from anchor presence (Story 293)
 * - Sections WITH anchors -> 'requirement' (need G1 coverage)
 * - Sections WITHOUT anchors -> 'informational' (excluded from G1)
 */
export function inferPRDTypeFromAnchor(anchor) {
    if (anchor && anchor.trim().length > 0) {
        return 'requirement';
    }
    return 'informational';
}
